/**
 * GameDetailModal — shows game details with per-user editing.
 * Current user's progress is always expanded; other users are collapsible.
 * Users are loaded dynamically from the UsersProvider context.
 */

import { useState, useEffect, useCallback } from 'react';
import { DetailModal, formatDate, formatDateShort, StarRating, CustomSelect } from '@code-burrow/design-system';
import { useUsers, deriveUserLabels, useUserView, useAddToList, usePod, PodMemberPicker, useCurrentUser } from '@code-burrow/tracker-core';
import { GameStatus } from '../models/GameEntry.js';
import { PlatformBadge, OWNERSHIP_PLATFORMS } from '../adapters/gameCollectionAdapter.jsx';

const STATUS_OPTIONS = [
    { value: GameStatus.NOT_STARTED,  label: 'Not played',   pillClassName: 'bg-zinc-500/15 text-zinc-400' },
    { value: GameStatus.WANT_TO_PLAY, label: 'Want to play', pillClassName: 'bg-sky-500/15 text-sky-400' },
    { value: GameStatus.PLAYING,      label: 'Playing',      pillClassName: 'bg-blue-500/15 text-blue-400' },
    { value: GameStatus.DIDNT_FINISH, label: 'Shelved',      pillClassName: 'bg-amber-500/15 text-amber-400' },
    { value: GameStatus.ABANDONED,    label: 'Abandoned',    pillClassName: 'bg-orange-500/15 text-orange-400' },
    { value: GameStatus.COMPLETED,    label: 'Completed',    pillClassName: 'bg-emerald-500/15 text-emerald-300' },
];

/** Format playtime minutes into a human-readable string */
function formatPlayTime(minutes) {
    if (!minutes || minutes <= 0) return null;
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours >= 100) return `${hours}h`;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

const USER_FIELDS = [
    { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
    { key: 'completedDate', label: 'Completed Date', type: 'date' },
    { key: 'rating', label: 'Rating', type: 'rating' },
    { key: 'lastPlayed', label: 'Last Played', type: 'date' },
    { key: 'playTime', label: 'Play Time (min)', type: 'number' },
    { key: 'earnedAchievements', label: 'Achievements', type: 'achievements' },
    { key: 'progress', label: 'Progress %', type: 'number' },
];

/**
 * Get user field value from an item.
 * Gets a user data field from an item's _userData map.
 */
function getUserField(item, userName, fieldName) {
    if (item._userData?.[userName]) {
        return item._userData[userName][fieldName] ?? null;
    }
    return null;
}


/**
 * InlineField — a single field that shows its value and becomes editable on tap.
 * Toggles and ratings are always interactive (no edit state).
 * Dates, selects, numbers, and achievements open an input on tap, save on blur/change.
 */
function InlineField({ field, value, onSave, disabled, extraProps }) {
    const [editing, setEditing] = useState(false);
    const [localVal, setLocalVal] = useState('');

    const startEdit = () => {
        if (disabled) return;
        if (field.type === 'date') {
            setLocalVal(value ? new Date(value).toISOString().split('T')[0] : '');
        } else if (field.type === 'select') {
            setLocalVal(value || '');
        } else if (field.type === 'number') {
            setLocalVal(value != null ? String(value) : '');
        } else if (field.type === 'text') {
            setLocalVal(value || '');
        }
        setEditing(true);
    };

    const commitDate = (val) => {
        const parsed = val ? new Date(val).toISOString() : null;
        if (parsed !== (value ? new Date(value).toISOString() : null)) {
            onSave(field.key, parsed);
        }
        setEditing(false);
    };

    const commitSelect = (val) => {
        if (val !== (value || '')) {
            onSave(field.key, val || null);
        }
        setEditing(false);
    };

    const commitNumber = (val) => {
        const parsed = val !== '' ? parseInt(val, 10) : null;
        if (parsed !== (value ?? null)) {
            onSave(field.key, parsed);
        }
        setEditing(false);
    };

    const commitText = (val) => {
        if (val !== (value || '')) {
            onSave(field.key, val || null);
        }
        setEditing(false);
    };

    // ── Toggle: always interactive, no edit state ──
    if (field.type === 'toggle') {
        const isOn = !!value;
        return (
            <div className="flex justify-between items-center min-h-[24px]">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={isOn}
                    disabled={disabled}
                    onClick={() => onSave(field.key, !isOn)}
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors duration-200 ${
                        isOn ? 'bg-accent-primary/60' : 'bg-bg-tertiary border border-border-subtle'
                    } ${disabled ? 'opacity-50' : ''}`}
                >
                    <span className={`inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        isOn ? 'translate-x-[14px]' : 'translate-x-0.5'
                    }`} />
                </button>
            </div>
        );
    }

    // ── Rating: always interactive, tap stars ──
    if (field.type === 'rating') {
        const rating = value || 0;
        return (
            <div className="flex justify-between items-center min-h-[24px]">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            disabled={disabled}
                            onClick={() => onSave(field.key, rating === n ? null : n)}
                            className={`text-base leading-none transition-colors ${
                                n <= rating ? 'text-yellow-400' : 'text-text-tertiary/30 hover:text-text-tertiary/60'
                            } ${disabled ? 'opacity-50' : ''}`}
                        >&#9733;</button>
                    ))}
                </div>
            </div>
        );
    }

    // ── Date: tap to open picker ──
    if (field.type === 'date') {
        if (editing) {
            return (
                <div className="flex justify-between items-center min-h-[24px]">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <div className="flex items-center gap-1">
                        <input
                            type="date"
                            value={localVal}
                            onChange={(e) => setLocalVal(e.target.value)}
                            onBlur={() => commitDate(localVal)}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitDate(localVal); if (e.key === 'Escape') setEditing(false); }}
                            autoFocus
                            className="h-[22px] px-2 rounded-sm bg-bg-primary border border-border text-text-primary text-xs focus:outline-none w-[130px] [color-scheme:dark]"
                        />
                        {localVal && (
                            <button
                                type="button"
                                onClick={() => { setLocalVal(''); commitDate(''); }}
                                className="text-text-tertiary hover:text-text-secondary"
                                title="Clear"
                            >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div
                className="flex justify-between items-center min-h-[24px] cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={startEdit}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary">{value ? formatDate(value) : '—'}</span>
            </div>
        );
    }

    // ── Select: custom dropdown ──
    if (field.type === 'select') {
        return (
            <div className="flex justify-between items-center min-h-[24px] -mx-1 px-1">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <CustomSelect
                    value={value || ''}
                    options={field.options}
                    onChange={(val) => {
                        if ((val || null) !== (value || null)) {
                            onSave(field.key, val || null);
                        }
                    }}
                    placeholder="—"
                    disabled={disabled}
                    triggerClassName="text-xs text-text-secondary hover:text-text-primary transition-colors duration-150"
                />
            </div>
        );
    }

    // ── Number: tap to reveal input, save on blur/Enter ──
    if (field.type === 'number') {
        if (editing) {
            return (
                <div className="flex justify-between items-center min-h-[24px]">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <div className="flex items-center gap-1">
                        <input
                            type="number"
                            value={localVal}
                            onChange={(e) => setLocalVal(e.target.value)}
                            onBlur={() => commitNumber(localVal)}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitNumber(localVal); if (e.key === 'Escape') setEditing(false); }}
                            autoFocus
                            min="0"
                            className="h-[22px] px-2 rounded-sm bg-bg-primary border border-border text-text-primary text-xs focus:outline-none w-[80px] [color-scheme:dark]"
                        />
                        {localVal && (
                            <button
                                type="button"
                                onClick={() => { setLocalVal(''); commitNumber(''); }}
                                className="text-text-tertiary hover:text-text-secondary"
                                title="Clear"
                            >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            );
        }
        let displayVal = '—';
        if (field.key === 'playTime' && value) {
            displayVal = formatPlayTime(value) || `${value}m`;
        } else if (field.key === 'progress' && value != null) {
            displayVal = `${value}%`;
        } else if (value != null) {
            displayVal = String(value);
        }
        return (
            <div
                className="flex justify-between items-center min-h-[24px] cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={startEdit}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary">{displayVal}</span>
            </div>
        );
    }

    // ── Text: tap to reveal text input, save on blur/Enter ──
    if (field.type === 'text') {
        if (editing) {
            return (
                <div className="flex justify-between items-center min-h-[24px]">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <input
                        type="text"
                        value={localVal}
                        onChange={(e) => setLocalVal(e.target.value)}
                        onBlur={() => commitText(localVal)}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitText(localVal); if (e.key === 'Escape') setEditing(false); }}
                        autoFocus
                        className="h-[22px] px-2 rounded-sm bg-bg-primary border border-border text-text-primary text-xs focus:outline-none w-[130px] [color-scheme:dark]"
                    />
                </div>
            );
        }
        return (
            <div
                className="flex justify-between items-center min-h-[24px] cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={startEdit}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary">{value || '—'}</span>
            </div>
        );
    }

    // ── Achievements: tap to reveal earned/total inputs ──
    if (field.type === 'achievements') {
        const earned = extraProps?.earned;
        const total = extraProps?.total;
        if (editing) {
            return (
                <div className="flex justify-between items-center min-h-[24px]">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <div className="flex items-center gap-1">
                        <input
                            type="number"
                            value={localVal}
                            onChange={(e) => setLocalVal(e.target.value)}
                            onBlur={() => {
                                const earnedVal = localVal !== '' ? parseInt(localVal, 10) : null;
                                if (earnedVal !== (earned ?? null)) {
                                    onSave('earnedAchievements', earnedVal);
                                }
                                setEditing(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const earnedVal = localVal !== '' ? parseInt(localVal, 10) : null;
                                    if (earnedVal !== (earned ?? null)) {
                                        onSave('earnedAchievements', earnedVal);
                                    }
                                    setEditing(false);
                                }
                                if (e.key === 'Escape') setEditing(false);
                            }}
                            autoFocus
                            min="0"
                            placeholder="Earned"
                            className="h-[22px] px-2 rounded-sm bg-bg-primary border border-border text-text-primary text-xs focus:outline-none w-[60px] [color-scheme:dark]"
                        />
                        <span className="text-xs text-text-tertiary">/</span>
                        <span className="text-xs text-text-secondary">{total ?? '?'}</span>
                    </div>
                </div>
            );
        }
        let displayVal = '—';
        if (earned != null || total != null) {
            displayVal = total ? `${earned ?? 0} / ${total}` : `${earned}`;
        }
        return (
            <div
                className="flex justify-between items-center min-h-[24px] cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={() => {
                    if (disabled) return;
                    setLocalVal(earned != null ? String(earned) : '');
                    setEditing(true);
                }}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary">{displayVal}</span>
            </div>
        );
    }

    return null;
}

/** Ownership row — shows platform icons that toggle on/off per user */
function OwnershipRow({ game, userName, onToggle, saving }) {
    const ownership = getUserField(game, userName, 'ownership') || [];
    return (
        <div className="flex items-center justify-between">
            <span className="text-xs text-text-tertiary">Owned on</span>
            <div className="flex items-center gap-1.5">
                {OWNERSHIP_PLATFORMS.map((p) => {
                    const owned = ownership.includes(p.key);
                    return (
                        <button
                            key={p.key}
                            onClick={() => {
                                const next = owned
                                    ? ownership.filter((k) => k !== p.key)
                                    : [...ownership, p.key];
                                onToggle(userName, next);
                            }}
                            disabled={saving}
                            className={`p-1 rounded transition-all duration-150 ${
                                owned
                                    ? 'opacity-100 ring-1 ring-accent-primary/40 bg-surface-hover'
                                    : 'opacity-30 hover:opacity-60'
                            } disabled:pointer-events-none`}
                            title={`${owned ? 'Remove from' : 'Add to'} ${p.label}`}
                        >
                            <PlatformBadge source={p.key} className="w-4 h-4" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function UserProgressSection({ game, users, labels, onSave, onRefreshStats, onOwnershipToggle, saving, currentUser, podMemberPicker, coSelectedIds = new Set() }) {
    const [expandedUsers, setExpandedUsers] = useState(new Set());

    /** Get visible fields — hide completedDate unless status is Completed */
    const getVisibleFields = (status) =>
        USER_FIELDS.filter((f) => f.key !== 'completedDate' || status === GameStatus.COMPLETED);

    /** Save a single field for a single user */
    const saveField = (userName, fieldKey, value) => {
        const updates = { [fieldKey]: value };
        // Auto-set completedDate when marking as Completed (if not already set)
        if (fieldKey === 'status' && value === GameStatus.COMPLETED) {
            const existing = getUserField(game, userName, 'completedDate');
            if (!existing) {
                updates.completedDate = getUserField(game, userName, 'lastPlayed') || new Date().toISOString();
            }
        }
        // Clear completedDate if status is no longer Completed
        if (fieldKey === 'status' && value && value !== GameStatus.COMPLETED) {
            updates.completedDate = null;
        }
        onSave(userName, updates);
    };

    /** Render a single InlineField for a given user, handling achievements specially */
    const renderField = (f, userName) => {
        if (f.type === 'achievements') {
            return (
                <InlineField
                    key={f.key}
                    field={f}
                    value={getUserField(game, userName, 'earnedAchievements')}
                    onSave={(fieldKey, val) => saveField(userName, fieldKey, val)}
                    disabled={saving}
                    extraProps={{
                        earned: getUserField(game, userName, 'earnedAchievements'),
                        total: getUserField(game, userName, 'totalAchievements'),
                    }}
                />
            );
        }
        return (
            <InlineField
                key={f.key}
                field={f}
                value={getUserField(game, userName, f.key)}
                onSave={(fieldKey, val) => saveField(userName, fieldKey, val)}
                disabled={saving}
            />
        );
    };

    const meUser = users.find(u => u.name === currentUser?.name);

    // Co-selected users (played together via PodMemberPicker)
    const coUsers = users.filter(u => u.name !== currentUser?.name && coSelectedIds.has(u.id));

    // Check if a pod member has meaningful activity (not just "Not played")
    const hasActivity = (userName) => {
        const status = getUserField(game, userName, 'status');
        const lastPlayed = getUserField(game, userName, 'lastPlayed');
        const rating = getUserField(game, userName, 'rating');
        const playTime = getUserField(game, userName, 'playTime');
        return !!((status && status !== 'Not played') || lastPlayed || rating || playTime);
    };

    // "From your pods" = others with independent activity who are NOT co-selected
    const othersWithActivity = users.filter(u =>
        u.name !== currentUser?.name && !coSelectedIds.has(u.id) && hasActivity(u.name)
    );

    const toggleExpanded = (userName) => {
        setExpandedUsers((prev) => {
            const next = new Set(prev);
            if (next.has(userName)) next.delete(userName);
            else next.add(userName);
            return next;
        });
    };

    const renderRefreshButton = (u, isMe) => {
        const canRefresh = u.steamId || game.steamID?.startsWith('xbox_') || game.apiInfo?.xbox?.titleId;
        if (!canRefresh) return null;
        return (
            <button
                onClick={() => onRefreshStats?.(u.name)}
                disabled={saving}
                className="p-1 rounded text-text-tertiary hover:text-accent-primary hover:bg-surface-hover transition-colors duration-150 disabled:opacity-50"
                aria-label={`Refresh ${isMe ? 'your' : (labels[u.name] || u.displayName) + "'s"} stats`}
                title="Refresh stats"
            >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
            </button>
        );
    };

    const renderUserFields = (userName, isMe) => (
        <div className="space-y-0.5">
            {getVisibleFields(getUserField(game, userName, 'status'))
                .filter((f) => ['status', 'completedDate', 'rating', 'lastPlayed'].includes(f.key))
                .map((f) => renderField(f, userName))}
            <OwnershipRow game={game} userName={userName} onToggle={onOwnershipToggle} saving={saving} />
            {getVisibleFields(getUserField(game, userName, 'status'))
                .filter((f) => ['playTime', 'earnedAchievements', 'progress'].includes(f.key))
                .map((f) => renderField(f, userName))}
            {isMe && podMemberPicker}
        </div>
    );

    // Build the "You & ..." header
    const togetherLabel = coUsers.length > 0
        ? `You & ${coUsers.map(u => labels[u.name] || u.displayName).join(' & ')}`
        : 'You';
    const togetherEmojis = meUser
        ? [meUser, ...coUsers].map(u => u.emoji).filter(Boolean)
        : [];

    return (
        <div className="space-y-4">
            <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide">Progress</h4>
            {/* Current user + co-selected together */}
            {meUser && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                            <div className="flex -space-x-1.5">
                                {togetherEmojis.map((emoji, i) => (
                                    <span key={i} className="text-sm leading-none">{emoji}</span>
                                ))}
                            </div>
                            <span className="text-sm font-medium text-text-primary">{togetherLabel}</span>
                        </div>
                        {renderRefreshButton(meUser, true)}
                    </div>
                    {renderUserFields(meUser.name, true)}
                </div>
            )}
            {/* Pod members with independent activity (not co-selected) */}
            <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide pt-2">From your pods</h4>
            {othersWithActivity.length === 0 ? (
                <p className="text-xs text-text-tertiary italic">No one else in your pods has played this yet</p>
            ) : (
                othersWithActivity.map((u) => {
                    const isExpanded = expandedUsers.has(u.name);
                    return (
                        <div key={u.name} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => toggleExpanded(u.name)}
                                    className="flex items-center gap-1.5 text-left cursor-pointer"
                                >
                                    <span className="text-sm">{u.emoji}</span>
                                    <span className="text-sm font-medium text-text-primary">
                                        {labels[u.name] || u.displayName}
                                    </span>
                                    {!isExpanded && (() => {
                                        const status = getUserField(game, u.name, 'status');
                                        const date = formatDateShort(getUserField(game, u.name, 'lastPlayed'));
                                        const parts = [];
                                        if (status) parts.push(status);
                                        if (date) parts.push(date);
                                        return parts.length > 0 ? (
                                            <span className="text-xs text-text-tertiary ml-auto mr-1">{parts.join(' · ')}</span>
                                        ) : null;
                                    })()}
                                    <svg className={`h-3.5 w-3.5 text-text-tertiary transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {isExpanded && renderRefreshButton(u, false)}
                            </div>
                            {isExpanded && renderUserFields(u.name, false)}
                        </div>
                    );
                })
            )}
        </div>
    );
}

export default function GameDetailModal({ game: initialGame, backendService, onClose, onDeleted, onSaved }) {
    const [saving, setSaving] = useState(false);
    const [liveGame, setLiveGame] = useState(initialGame);
    const { users: allUsers } = useUsers();
    const { allowedUsers } = usePod();
    const allowed = allowedUsers('videogames');
    const users = allowed ? allUsers.filter(u => allowed.includes(u.id)) : allUsers;
    const labels = deriveUserLabels(users);
    const { activeView } = useUserView();
    const currentUser = useCurrentUser();
    const [coSelectedIds, setCoSelectedIds] = useState(new Set());

    const { addToListAction } = useAddToList({
        backendService,
        activeView,
        getItemData: useCallback(() => ({
            itemId: liveGame.id,
            title: liveGame.title,
            imageURL: liveGame.imageURL || '',
        }), [liveGame]),
    });

    // Keep in sync if parent passes a new game — but only if it has _userData
    // (avoids overwriting with incomplete data during parent re-fetch)
    useEffect(() => {
        if (initialGame._userData && Object.keys(initialGame._userData).length > 0) {
            setLiveGame(initialGame);
        }
    }, [initialGame]);

    // Re-fetch the game from DB (with user data) so the modal reflects the latest data.
    // Returns the merged game (or null) so callers can propagate it to the grid.
    // Non-clobbering: when the user_games read comes back empty (e.g. a locked
    // collection blocks the direct read), the merge keeps any prior/optimistic
    // _userData rather than wiping it.
    const refreshGame = useCallback(async () => {
        const result = await backendService.getGame(liveGame.steamID);
        if (result.success && result.entry) {
            // Also fetch user_games records for this item and merge _userData
            const userGamesResult = await backendService.getUserGamesForItem(result.entry.id);
            const userDataMap = {};
            if (userGamesResult.records?.length > 0) {
                for (const ugr of userGamesResult.records) {
                    const u = users.find((usr) => usr.id === ugr.user);
                    if (!u) continue;
                    userDataMap[u.name] = {};
                    for (const f of ['status', 'rating', 'playTime', 'lastPlayed', 'completedDate', 'earnedAchievements', 'totalAchievements', 'progress', 'ownership']) {
                        userDataMap[u.name][f] = ugr[f] ?? null;
                    }
                }
            }
            const merged = { ...result.entry, _userData: { ...liveGame._userData, ...userDataMap } };
            // Use functional update to avoid stale closure over liveGame._userData
            setLiveGame((prev) => ({ ...result.entry, _userData: { ...prev._userData, ...userDataMap } }));
            return merged;
        }
        return null;
    }, [backendService, liveGame.steamID, liveGame._userData, users]);

    // Merge a saved field change into liveGame._userData immediately so the open
    // modal reflects the edit without waiting on (or depending on) a re-read.
    const applyOptimistic = (userName, updates) => {
        const updatedGame = {
            ...liveGame,
            _userData: {
                ...liveGame._userData,
                [userName]: { ...(liveGame._userData?.[userName] || {}), ...updates },
            },
        };
        setLiveGame(updatedGame);
        return updatedGame;
    };

    // On initial open: if the item arrived without user data (e.g. just added from search),
    // fetch it now so status/rating/etc. are populated.
    useEffect(() => {
        if (!initialGame._userData || Object.keys(initialGame._userData).length === 0) {
            refreshGame();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUserSave = async (userName, updates) => {
        setSaving(true);
        try {
            const result = await backendService.upsertUserGame(userName, liveGame.id, updates);
            if (!result.success) throw new Error(result.error || 'Failed to save');
            // Reflect the change immediately in the modal + grid, then reconcile.
            const updatedGame = applyOptimistic(userName, updates);
            onSaved?.(updatedGame);
            refreshGame();
        } finally {
            setSaving(false);
        }
    };

    const handleOwnershipToggle = async (userName, newOwnership) => {
        setSaving(true);
        try {
            await backendService.upsertUserGame(userName, liveGame.id, { ownership: newOwnership });
            const updatedGame = applyOptimistic(userName, { ownership: newOwnership });
            onSaved?.(updatedGame);
            refreshGame();
        } finally {
            setSaving(false);
        }
    };

    const handleRefreshStats = async (userName) => {
        const user = users.find((u) => u.name === userName);
        const hasXboxData = liveGame.steamID?.startsWith('xbox_') || liveGame.apiInfo?.xbox?.titleId;
        // Need either a steamId (for Steam games) or Xbox data
        if (!hasXboxData && !user?.steamId) return;
        setSaving(true);
        try {
            const result = await backendService.refreshUserStats(liveGame.steamID, userName, user?.steamId, {
                apiInfo: liveGame.apiInfo,
            });
            if (!result.success) throw new Error(result.error || 'Failed to refresh stats');
            const refreshed = await refreshGame();
            onSaved?.(refreshed ?? liveGame);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        // Delete user_games join records first (foreign key constraint)
        try {
            const joins = await backendService.pocketbase.collection('user_games').getFullList({
                filter: `game="${liveGame.id}"`, $autoCancel: false,
            });
            for (const j of joins) await backendService.proxy.collection('user_games').delete(j.id);
        } catch { /* no join records to delete */ }
        await backendService.deleteGame(liveGame.steamID);
    };

    const menuActions = [
        addToListAction,
        {
            label: 'Refresh Metadata',
            onClick: async () => {
                const result = await backendService.refreshGame(liveGame.steamID);
                if (result && !result.success) throw new Error(result.error);
                const refreshed = await refreshGame();
                onSaved?.(refreshed ?? liveGame);
            },
        },
    ];

    return (
        <DetailModal
            item={liveGame}
            onClose={onClose}
            onSaved={onSaved}
            onDeleted={onDeleted}
            onSave={() => {}} // editing handled by UserProgressSection
            onDelete={handleDelete}
            fields={[]}
            editable={false}
            menuActions={menuActions}
            extraContent={
                <>
                    <UserProgressSection
                        game={liveGame}
                        users={users}
                        labels={labels}
                        onSave={handleUserSave}
                        onRefreshStats={handleRefreshStats}
                        onOwnershipToggle={handleOwnershipToggle}
                        saving={saving}
                        currentUser={currentUser}
                        coSelectedIds={coSelectedIds}
                        podMemberPicker={
                            <PodMemberPicker
                                itemId={liveGame.id}
                                altItemIds={liveGame.steamID ? [liveGame.steamID] : undefined}
                                itemType="game"
                                itemTitle={liveGame.title}
                                activityType="played"
                                label="Played with"
                                onChanged={(selectedIds) => {
                                    setCoSelectedIds(selectedIds);
                                    onSaved?.();
                                }}
                            />
                        }
                    />
                </>
            }
            header={{
                imageUrl: liveGame.imageURL,
                imageAlt: liveGame.title,
                title: liveGame.title,
                subtitle: liveGame.creator,
                tags: liveGame.genres?.slice(0, 4),
                extra: (
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
                        {liveGame.releaseDate && (
                            <span>{formatDate(liveGame.releaseDate)}</span>
                        )}
                        {liveGame.isDLC && (
                            <span className="px-1.5 py-0.5 rounded bg-accent-primary/10 text-cyan-300 font-medium">DLC</span>
                        )}
                        {liveGame.storeURL && (
                            <a
                                href={liveGame.storeURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-accent-primary hover:text-accent-hover transition-colors duration-150"
                            >
                                {liveGame.source === 'rawg' ? 'View on RAWG' : liveGame.source === 'xbox' ? 'View on RAWG' : 'View on Steam'}
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        )}
                        <span className="flex-1" /><PlatformBadge source={liveGame.source} className="w-4 h-4" />
                    </div>
                ),
            }}
        />
    );
}
